import Phaser from 'phaser';
import {
  COLORS,
  DEPTHS,
  DISPUTE_FORM_MODAL,
  FONTS,
  GAME_HEIGHT,
  GAME_WIDTH,
  toHexColor,
} from '../config/gameConfig';
import { COPY } from '../content/copy';
import type { DisputeHook, DisputeType, EventBus } from '../systems/EventBus';
import { renderHelpLink } from './HelpLink';
import { propagateDepth } from './propagateDepth';

type ChoiceKey = 'poMismatch' | 'amount' | 'billingError' | 'other';

const CHOICE_ORDER: readonly { key: ChoiceKey; id: DisputeType }[] = [
  { key: 'poMismatch', id: 'po-mismatch' },
  { key: 'amount', id: 'amount' },
  { key: 'billingError', id: 'billing-error' },
  { key: 'other', id: 'other' },
];

interface RadioRef {
  id: DisputeType;
  outer: Phaser.GameObjects.Arc;
  inner: Phaser.GameObjects.Arc;
  row: Phaser.GameObjects.Rectangle;
}

export class DisputeFormModal {
  private beatFired = false;
  private pendingHook: DisputeHook | null = null;
  private pendingEmailId: string | null = null;
  private activeHook: DisputeHook | null = null;
  private activeEmailId: string | null = null;
  private selected: DisputeType | null = null;
  private container: Phaser.GameObjects.Container | null = null;
  private escHandler: ((ev: KeyboardEvent) => void) | null = null;
  private radios: RadioRef[] = [];
  private submitButton: Phaser.GameObjects.Rectangle | null = null;
  private submitLabel: Phaser.GameObjects.Text | null = null;

  private readonly onBeat = (p: { beatId: string }) => {
    if (p.beatId === 'beat-5-dispute') {
      this.beatFired = true;
      this.maybeOpen();
    }
  };
  private readonly onActionClicked = (p: { emailId: string; hook: DisputeHook }) => {
    this.pendingHook = p.hook;
    this.pendingEmailId = p.emailId;
    this.maybeOpen();
  };

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly bus: EventBus,
    _tooltip?: import('./Tooltip').Tooltip,
  ) {
    bus.on('beat:triggered', this.onBeat);
    bus.on('dispute:email-action-clicked', this.onActionClicked);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  chooseForTest(disputeType: DisputeType): void {
    this.select(disputeType);
    this.submit();
  }

  cancelForTest(): void {
    this.close();
  }

  private maybeOpen(): void {
    if (!this.beatFired) return;
    if (!this.pendingHook || !this.pendingEmailId) return;
    if (this.container !== null) return;
    this.activeHook = this.pendingHook;
    this.activeEmailId = this.pendingEmailId;
    this.pendingHook = null;
    this.pendingEmailId = null;
    this.open();
  }

  private open(): void {
    if (!this.activeHook || !this.activeEmailId) return;
    this.bus.emit('term:unlocked', { term: 'dispute' });
    this.bus.emit('dispute:form-opened', {
      emailId: this.activeEmailId,
      customer: this.activeHook.customer,
      invoiceId: this.activeHook.invoiceId,
    });

    const x = Math.round((GAME_WIDTH - DISPUTE_FORM_MODAL.width) / 2);
    const y = Math.round((GAME_HEIGHT - DISPUTE_FORM_MODAL.height) / 2);

    const root = this.scene.add.container(0, 0);
    root.setDepth(DEPTHS.modal);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, COLORS.black, DISPUTE_FORM_MODAL.backdropAlpha)
      .setOrigin(0, 0);
    root.add(backdrop);

    const panel = this.scene.add
      .rectangle(x, y, DISPUTE_FORM_MODAL.width, DISPUTE_FORM_MODAL.height, COLORS.windowBody)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.windowBorder)
      .setInteractive();
    root.add(panel);

    const titlePrefix = COPY.dispute.title.split('—')[0].trim();
    const titleText = `${titlePrefix} — ${this.activeHook.customer} · ${this.activeHook.invoiceId}`;
    const title = this.scene.add.text(
      x + DISPUTE_FORM_MODAL.paddingX,
      y + DISPUTE_FORM_MODAL.paddingY,
      titleText,
      {
        fontFamily: FONTS.ui,
        fontSize: DISPUTE_FORM_MODAL.titleFontSize,
        color: toHexColor(COLORS.textPrimary),
        fontStyle: 'bold',
      },
    );
    root.add(title);

    renderHelpLink(
      this.scene,
      root,
      x + DISPUTE_FORM_MODAL.width - DISPUTE_FORM_MODAL.paddingX,
      y + DISPUTE_FORM_MODAL.paddingY + 4,
      'dispute',
      this.bus,
    );

    const subpromptY = y + DISPUTE_FORM_MODAL.paddingY + title.height + 10;
    const subprompt = this.scene.add.text(
      x + DISPUTE_FORM_MODAL.paddingX,
      subpromptY,
      COPY.dispute.subprompt,
      {
        fontFamily: FONTS.ui,
        fontSize: DISPUTE_FORM_MODAL.subpromptFontSize,
        color: toHexColor(COLORS.textPrimary),
      },
    );
    root.add(subprompt);

    // Lookup hint — point the player at the email body and the spreadsheet
    // for the dispute classification.
    const hintY = subpromptY + subprompt.height + 6;
    const hint = this.scene.add.text(
      x + DISPUTE_FORM_MODAL.paddingX,
      hintY,
      '↗ Re-read the customer\'s email and check the invoice in the AR aging spreadsheet to classify the dispute.',
      {
        fontFamily: FONTS.ui,
        fontSize: '11px',
        color: toHexColor(COLORS.accentWarn),
        fontStyle: 'italic',
        wordWrap: { width: DISPUTE_FORM_MODAL.width - DISPUTE_FORM_MODAL.paddingX * 2 },
      },
    );
    root.add(hint);

    const rowWidth = DISPUTE_FORM_MODAL.width - DISPUTE_FORM_MODAL.paddingX * 2;
    let rowY = hintY + hint.height + 10;

    this.radios = [];
    for (const choice of CHOICE_ORDER) {
      const copy = COPY.dispute.choices[choice.key];
      const row = this.scene.add
        .rectangle(
          x + DISPUTE_FORM_MODAL.paddingX,
          rowY,
          rowWidth,
          DISPUTE_FORM_MODAL.choiceRowHeight,
          COLORS.windowChrome,
        )
        .setOrigin(0, 0)
        .setStrokeStyle(1, COLORS.windowBorder)
        .setInteractive({ useHandCursor: true });
      root.add(row);

      const radioCx = x + DISPUTE_FORM_MODAL.paddingX + DISPUTE_FORM_MODAL.radioMarginX;
      const radioCy = rowY + DISPUTE_FORM_MODAL.radioMarginY;
      const outer = this.scene.add
        .circle(radioCx, radioCy, DISPUTE_FORM_MODAL.radioRadius, COLORS.windowBody)
        .setStrokeStyle(1, COLORS.windowBorder);
      root.add(outer);
      const inner = this.scene.add
        .circle(radioCx, radioCy, DISPUTE_FORM_MODAL.radioInnerRadius, COLORS.buttonPrimary)
        .setVisible(false);
      root.add(inner);

      const labelX = radioCx + DISPUTE_FORM_MODAL.radioRadius + 12;
      const label = this.scene.add.text(labelX, rowY + 8, copy.label, {
        fontFamily: FONTS.ui,
        fontSize: DISPUTE_FORM_MODAL.choiceLabelFontSize,
        color: toHexColor(COLORS.textPrimary),
        fontStyle: 'bold',
      });
      root.add(label);

      const blurb = this.scene.add.text(
        labelX,
        rowY + 8 + label.height + 4,
        copy.blurb,
        {
          fontFamily: FONTS.ui,
          fontSize: DISPUTE_FORM_MODAL.choiceBlurbFontSize,
          color: toHexColor(COLORS.textMuted),
          wordWrap: { width: rowWidth - (labelX - (x + DISPUTE_FORM_MODAL.paddingX)) - 12 },
        },
      );
      root.add(blurb);

      this.radios.push({ id: choice.id, outer, inner, row });

      row.on('pointerdown', () => this.select(choice.id));

      rowY += DISPUTE_FORM_MODAL.choiceRowHeight + DISPUTE_FORM_MODAL.choiceRowGap;
    }

    const amountLabelY = rowY + 6;
    const amountLabel = this.scene.add.text(
      x + DISPUTE_FORM_MODAL.paddingX,
      amountLabelY,
      `${COPY.dispute.amountLabel}:`,
      {
        fontFamily: FONTS.ui,
        fontSize: DISPUTE_FORM_MODAL.amountLabelFontSize,
        color: toHexColor(COLORS.textMuted),
      },
    );
    root.add(amountLabel);

    const amountValue = this.scene.add.text(
      x + DISPUTE_FORM_MODAL.paddingX + amountLabel.width + 10,
      amountLabelY - 2,
      this.formatAmount(this.activeHook.amountSuggested),
      {
        fontFamily: FONTS.ui,
        fontSize: DISPUTE_FORM_MODAL.amountValueFontSize,
        color: toHexColor(COLORS.textPrimary),
        fontStyle: 'bold',
      },
    );
    root.add(amountValue);

    const buttonsY = y + DISPUTE_FORM_MODAL.height - DISPUTE_FORM_MODAL.paddingY - DISPUTE_FORM_MODAL.submitButtonHeight;
    const submitX = x + DISPUTE_FORM_MODAL.width - DISPUTE_FORM_MODAL.paddingX - DISPUTE_FORM_MODAL.submitButtonWidth;
    const submitBg = this.scene.add
      .rectangle(submitX, buttonsY, DISPUTE_FORM_MODAL.submitButtonWidth, DISPUTE_FORM_MODAL.submitButtonHeight, COLORS.buttonPrimary)
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true })
      .setAlpha(0.4);
    submitBg.on('pointerdown', () => {
      if (this.selected !== null) this.submit();
    });
    root.add(submitBg);
    this.submitButton = submitBg;

    const submitLabel = this.scene.add.text(
      submitX + DISPUTE_FORM_MODAL.submitButtonWidth / 2,
      buttonsY + DISPUTE_FORM_MODAL.submitButtonHeight / 2,
      COPY.dispute.submitButton,
      {
        fontFamily: FONTS.ui,
        fontSize: DISPUTE_FORM_MODAL.submitButtonFontSize,
        color: toHexColor(COLORS.buttonPrimaryText),
        fontStyle: 'bold',
      },
    ).setOrigin(0.5, 0.5);
    root.add(submitLabel);
    this.submitLabel = submitLabel;

    const cancelX = submitX - DISPUTE_FORM_MODAL.buttonGap - DISPUTE_FORM_MODAL.cancelButtonWidth;
    const cancelBg = this.scene.add
      .rectangle(cancelX, buttonsY, DISPUTE_FORM_MODAL.cancelButtonWidth, DISPUTE_FORM_MODAL.cancelButtonHeight, COLORS.windowChrome)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.windowBorder)
      .setInteractive({ useHandCursor: true });
    cancelBg.on('pointerdown', () => this.close());
    root.add(cancelBg);

    const cancelLabel = this.scene.add.text(
      cancelX + DISPUTE_FORM_MODAL.cancelButtonWidth / 2,
      buttonsY + DISPUTE_FORM_MODAL.cancelButtonHeight / 2,
      COPY.dispute.cancelButton,
      {
        fontFamily: FONTS.ui,
        fontSize: DISPUTE_FORM_MODAL.cancelButtonFontSize,
        color: toHexColor(COLORS.textMuted),
      },
    ).setOrigin(0.5, 0.5);
    root.add(cancelLabel);

    this.escHandler = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') this.close();
    };
    this.scene.input.keyboard?.on('keydown', this.escHandler);

    this.container = root;
    propagateDepth(root, DEPTHS.modal);
  }

  private select(disputeType: DisputeType): void {
    this.selected = disputeType;
    for (const r of this.radios) {
      r.inner.setVisible(r.id === disputeType);
    }
    if (this.submitButton) this.submitButton.setAlpha(1);
    if (this.submitLabel) this.submitLabel.setAlpha(1);
  }

  private submit(): void {
    if (!this.selected || !this.activeHook || !this.activeEmailId) return;
    this.bus.emit('dispute:resolved', {
      emailId: this.activeEmailId,
      customer: this.activeHook.customer,
      invoiceId: this.activeHook.invoiceId,
      amount: this.activeHook.amountSuggested,
      disputeType: this.selected,
      reason: '',
    });
    this.showToast();
    this.close();
  }

  private showToast(): void {
    if (!this.activeHook) return;
    const toast = this.scene.add
      .text(
        GAME_WIDTH / 2,
        GAME_HEIGHT - 80,
        `${COPY.dispute.toastPrefix} ${this.activeHook.customer} · ${this.activeHook.invoiceId}`,
        {
          fontFamily: FONTS.ui,
          fontSize: DISPUTE_FORM_MODAL.toastFontSize,
          color: toHexColor(COLORS.textPrimary),
          backgroundColor: toHexColor(COLORS.windowChrome),
          padding: { x: 12, y: 8 },
        },
      )
      .setOrigin(0.5, 0.5)
      .setDepth(DEPTHS.modal);
    this.scene.time.delayedCall(DISPUTE_FORM_MODAL.toastDurationMs, () => toast.destroy());
  }

  private formatAmount(n: number): string {
    return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  private close(): void {
    if (this.escHandler) {
      this.scene.input.keyboard?.off('keydown', this.escHandler);
      this.escHandler = null;
    }
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
    this.radios = [];
    this.submitButton = null;
    this.submitLabel = null;
    this.selected = null;
    this.activeHook = null;
    this.activeEmailId = null;
  }

  private teardown(): void {
    this.bus.off('beat:triggered', this.onBeat);
    this.bus.off('dispute:email-action-clicked', this.onActionClicked);
    this.close();
  }
}
